import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import {
  User,
  UserRole,
  AuthResponse,
  AuthResponseLocal,
  LoginRequest,
} from '../../shared/models/auth.model';
import { environment } from '../../environments/environment';
import { NotificationService } from '../../shared/services/notification.service';
import { UsersService } from '../../shared/services/users.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  private currentUserSubject = new BehaviorSubject<User | null>(null);

  public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();
  public currentUser$ = this.currentUserSubject.asObservable();

  constructor(
    private http: HttpClient,
    private notificationService: NotificationService,
    private usersService: UsersService
  ) {
    // Clean up any legacy localStorage entries on service initialization
    if (localStorage.getItem('currentUser')) {
      localStorage.removeItem('currentUser');
    }
    
    // Initialize authentication state
    this.initializeAuthState();
  }

  private initializeAuthState(): void {
    const isAuth = this.isAuthenticated();
    const user = this.getCurrentUser();
    
    this.isAuthenticatedSubject.next(isAuth);
    this.currentUserSubject.next(user);
  }

  login(credentials: LoginRequest): Observable<AuthResponseLocal> {
    return this.http
      .post<AuthResponse>(`${environment.apiUrl}/Auth/login`, credentials)
      .pipe(
        switchMap((response: AuthResponse) => {
          localStorage.setItem('userEmail', response.email);
          localStorage.setItem('userName', response.fullName);
          localStorage.setItem('userRole', response.role);
          localStorage.setItem('authToken', response.token);

          return this.usersService.getUserDetailByEmail(response.email).pipe(
            map((detail) => {
              if (detail?.id) {
                localStorage.setItem('userId', detail.id);
              } else {
                localStorage.removeItem('userId');
              }

              const user: User = {
                email: response.email,
                name: response.fullName,
                role: response.role as UserRole,
                isActive: true,
                ...(detail?.id ? { id: detail.id } : {}),
              };

              this.isAuthenticatedSubject.next(true);
              this.currentUserSubject.next(user);

              return {
                user,
                token: response.token,
              };
            }),
            catchError(() => {
              localStorage.removeItem('userId');
              const user: User = {
                email: response.email,
                name: response.fullName,
                role: response.role as UserRole,
                isActive: true,
              };
              this.isAuthenticatedSubject.next(true);
              this.currentUserSubject.next(user);
              return of({
                user,
                token: response.token,
              });
            })
          );
        }),
        catchError((error) => {
          let errorMessage = 'Login failed. Please check your credentials.';
          if (error.error) {
            if (typeof error.error === 'string') {
              errorMessage = error.error;
            } else if (error.error.message) {
              errorMessage = error.error.message;
            }
          }
          return throwError(() => new Error(errorMessage));
        })
      );
  }

  logout(): void {
    // Clear notifications when user logs out
    this.notificationService.clearNotifications();
    
    // Check if Remember Me is enabled
    const rememberMe = localStorage.getItem('rememberMe');
    
    // Always clear session-specific data. Remember Me keeps the email only.
    localStorage.removeItem('userName');
    localStorage.removeItem('userRole');
    localStorage.removeItem('authToken');
    localStorage.removeItem('userId');
    localStorage.removeItem('rememberedPassword');
    
    if (rememberMe !== 'true') {
      localStorage.removeItem('userEmail');
      localStorage.removeItem('rememberedEmail');
    }
    
    // Remove any legacy localStorage entries that might exist
    localStorage.removeItem('currentUser');
    localStorage.removeItem('email');
    // Note: We do NOT remove 'rememberMe' here - it should persist to remember the preference

    // Emit authentication state changes
    this.isAuthenticatedSubject.next(false);
    this.currentUserSubject.next(null);
  }

  getCurrentUser(): User | null {
    // Check for authToken to ensure user is actually logged in
    // (userEmail may exist for Remember Me even when logged out)
    const token = localStorage.getItem('authToken');
    if (!token) {
      return null;
    }
    
    const email = localStorage.getItem('userEmail');
    const name = localStorage.getItem('userName');
    const role = localStorage.getItem('userRole');
    
    if (email && name && role) {
      const userId = localStorage.getItem('userId');
      return {
        email,
        name,
        role: role as UserRole,
        isActive: true,
        ...(userId ? { id: userId } : {}),
      };
    }
    return null;
  }

  /**
   * Persist API user id (GUID) and refresh `currentUser$` for features that match assignments by `userId`.
   */
  setStoredUserId(id: string): void {
    localStorage.setItem('userId', id);
    const user = this.getCurrentUser();
    if (user) {
      this.currentUserSubject.next({ ...user, id });
    }
  }

  isAuthenticated(): boolean {
    // Check authToken instead of userEmail since userEmail may persist for Remember Me
    return localStorage.getItem('authToken') !== null;
  }

  hasRole(role: UserRole): boolean {
    const currentRole = localStorage.getItem('userRole');
    return currentRole === role;
  }

  hasAnyRole(roles: UserRole[]): boolean {
    const currentRole = localStorage.getItem('userRole');
    return currentRole ? roles.includes(currentRole as UserRole) : false;
  }

  getToken(): string | null {
    return localStorage.getItem('authToken');
  }
}
